/* eslint-disable max-lines */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Selectable } from '@hakimo-ui/shared/ui-base';
import { FrameDetection, MatchedFace } from './unified-alarm';

export type MultiSelectFilterValue = {
  values: Selectable[];
  negative: boolean;
};
export type CameraMultiSelectFilterValue = {
  values: CameraSelectable[];
  negative: boolean;
};
export interface CameraSelectable extends Selectable {
  location_id?: string;
  location_name?: string;
}
export interface LocationSelectable extends Selectable {
  tenant_id?: string;
}
export type LocationMultiSelectFilterValue = {
  values: LocationSelectable[];
  negative: boolean;
};
export type EmployeeFilterValue = MultiSelectFilterValue;
export type LocationFilterValue = LocationMultiSelectFilterValue;
export type DoorNameFilterValue = MultiSelectFilterValue;
export type CameraFilterValue = CameraMultiSelectFilterValue;
export type Point = [number, number];
export type PointSize = 'small' | 'medium' | 'large';
export type Marker = { point: Point; color?: string; size?: PointSize };
export type Polygon = {
  points: Point[];
  color?: string;
  pointSize?: PointSize;
  closed?: boolean;
  tag?: string;
};
export type TenantFilterValue = {
  values: Selectable[];
  negative: boolean;
};

export type SourceEntityNameFilterValue = {
  values: Selectable[];
  negative: boolean;
};

export interface CameraDisarmWindow {
  timezone: string;
  startTime: number;
  endTime: number;
  days?: string[];
}

export interface ArmStatus {
  isArmed: boolean;
  endTimeText?: string;
  endTime?: number | string;
  cameraDisarmWindow?: CameraDisarmWindow;
}

export interface FrameData {
  id?: string;
  url: string;
  timestamp: number;
  matched_faces?: MatchedFace[];
  persons?: FrameDetection[];
  vehicles?: FrameDetection[];
  // Source alarm when many alarms' frames are merged into one continuous track
  // (unified alarm group) — lets face overlays stay scoped to the alarm playing.
  alarmId?: string;
  // The owning alarm's camera and type, carried per-frame because a merged
  // track spans several alarms. Surfaced on a matched face's comparison view.
  cameraName?: string;
  alarmType?: string;
}

export interface TableauUrlResponse {
  tableauUrl: string;
}

export interface Linkable {
  id: string;
  name: string;
}

export interface PagedResponseDTO<T> {
  items: T[];
  total: number;
}

export type HttpClient = <T>(
  requestInfo: RequestInfo,
  options?: RequestOptions<T>
) => Promise<T>;

export interface RequestOptions<T> {
  noAuth?: boolean;
  /** Use this function to run any side-effects */
  tap?: (response: Response) => void;
  responseModifier?: (response: Response) => Promise<T>;
}

export type VideoControlsList =
  | 'noFullscreen'
  | 'noVideoTimeline'
  | 'noLoader'
  | 'noPlaybackRate'
  | 'noForwardBackward'
  | 'noAudio'
  | 'onlyTime'
  | 'noPlayPause'
  | 'none';

export enum DetectionType {
  PERSON = 'person',
  VEHICLE = 'vehicle',
  INCORRECT_DETECTION = 'incorrect detection',
  CORRECT_DETECTION = 'correct detection',
  INCORRECT_DETECTION_V2 = 'Incorrect detection',
}

export interface StepperState {
  step: number;
  detectionType: DetectionType | undefined;
  comment: string;
}

export enum WsConnectionStatus {
  PENDING = 'pending',
  CONNECTED = 'connected',
  CLOSED = 'closed',
  ERROR = 'error',
  RECONNECTING = 'reconnecting',
}

export interface WsConnectionState {
  status: WsConnectionStatus;
  isDuplicateSession: boolean;
  retryAttempt?: number;
  nextRetryIn?: number;
  isRetrying?: boolean;
}

export interface WSConnection {
  connectionState: WsConnectionState;
  subscribe?: (topid: string, callback: (data: any) => void) => () => void; // returns an unsubscribe callback
  send?: (message: any) => void;
  disconnect?: () => void;
  reconnect?: () => void;
}

export interface LoiteringEntity {
  entity_id: string;
  description: string;
  first_seen: string;
  last_seen: string;
  triggered_at_utc?: string;
  dwell_minutes: number;
  appearance_minutes: number;
}

export interface LoiteringCamera {
  camera_id: string;
  camera_name: string;
  entities: LoiteringEntity[];
}

export interface AIRawState {
  type: string;
  cameras?: LoiteringCamera[];
}

export interface AIRecommendation {
  recommendations: string;
  summary: string;
  headline?: string;
  rawState?: AIRawState;
}

export enum WSDataType {
  DUPLICATE_CONNECTION = 'DuplicateConnection',
}

export interface RetryConfig {
  maxRetries: number;
  baseDelay: number;
  maxDelay: number;
  jitterEnabled: boolean;
}

export interface WebSocketClientOptions {
  /**
   * The key in the message data that indicates the type of event.
   * Defaults to 'event_type'.
   */
  dataTypeKey?: string;
  /**
   * An array of event types that should be batched.
   * If provided, messages of these types will be collected and sent in batches.
   */
  batchDataTypes?: string[];
  /**
   * The interval in milliseconds to flush batched messages.
   * Defaults to 1000ms (1 second).
   */
  flushInterval?: number;
  /**
   * Whether to allow duplicate sessions.
   * If true, the client will not consider a new connection as a duplicate session.
   */
  isDuplicateSessionAllowed?: boolean;
  /**
   * Retry configuration for exponential backoff.
   */
  retryConfig?: Partial<RetryConfig>;
  /**
   * Whether to enable automatic reconnection on connection loss.
   * Defaults to true.
   */
  autoReconnect?: boolean;
}
